import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { PageHeader } from '@/components/admin/AdminShell';
import { type Column, ListTable, type ListState } from '@/components/admin/ListTable';
import { Notice } from '@/components/admin/Notice';
import { ROLE_LABELS, type Role } from '@/features/auth/api';
import { fetchAuditLogs, type AuditLog } from '@/features/auditLogs/api';
import {
  AUDIT_ACTION_CHOICES,
  auditActionLabel,
  auditResourceLabel,
} from '@/features/auditLogs/labels';
import { ApiError } from '@/lib/api/http';
import { datetimeLocalToIso, formatDateTime } from '@/lib/datetime';

const PER_PAGE = 20;

const COLUMNS: Column<AuditLog>[] = [
  { key: 'at', header: '日時', primary: true, render: (log) => formatDateTime(log.at) },
  {
    key: 'actor',
    header: '実行者',
    render: (log) =>
      `${log.actor_name ?? '—'}（${log.actor_role ? ROLE_LABELS[log.actor_role as Role] ?? log.actor_role : '—'}）`,
  },
  // 内部コード（member.created 等）のままでは何の記録か分からない（D-086）
  { key: 'action', header: '操作', render: (log) => auditActionLabel(log.action) },
  { key: 'resource', header: '対象', render: (log) => auditResourceLabel(log.resource_type) },
  { key: 'reason', header: '理由', render: (log) => log.reason ?? '—' },
];

/**
 * 監査ログ検索（事務局・運営会社のみ / implementation-spec §12 M11 / DECISIONS D-044）。
 * 操作・実行者・期間で絞り込める。実行者は一覧の行から選ぶ（D-086。
 * 以前は user_id を手入力する欄で、事務局には誰のIDも分からず使えなかった）。
 */
export function AuditLogsPage(): ReactElement {
  const [page, setPage] = useState(1);
  const [actor, setActor] = useState<{ id: string; name: string } | null>(null);
  const [action, setAction] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const query = useQuery({
    queryKey: ['audit-logs', page, actor?.id, action, dateFrom, dateTo],
    queryFn: () =>
      fetchAuditLogs({
        page,
        per_page: PER_PAGE,
        actor_user_id: actor?.id ?? undefined,
        action: action || undefined,
        date_from: dateFrom ? datetimeLocalToIso(dateFrom) : undefined,
        date_to: dateTo ? datetimeLocalToIso(dateTo) : undefined,
      }),
  });

  let state: ListState = 'ready';
  if (query.isPending) state = 'loading';
  else if (query.isError) {
    state = query.error instanceof ApiError && query.error.status === 403 ? 'forbidden' : 'error';
  }

  return (
    <>
      <PageHeader
        title="監査ログ"
        description="「誰が・いつ・何をしたか」の記録です。公開・確定・権限変更・CSV取込などの重要な操作が自動で残ります。記録を消すことはできません。"
      />

      <div className="mb-4 flex flex-wrap items-end gap-2 rounded-sm border border-wpborder bg-white px-3 py-3">
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">操作</span>
          {/* 内部コードを知らないと検索できない状態だった（D-086） */}
          <select
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
            className="w-72 rounded-sm border border-wpborder px-3 py-2"
          >
            <option value="">すべての操作</option>
            {AUDIT_ACTION_CHOICES.map((choice) => (
              <option key={choice.action} value={choice.action}>
                {choice.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">期間（開始）</span>
          <input
            type="datetime-local"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            className="rounded-sm border border-wpborder px-3 py-2"
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">期間（終了）</span>
          <input
            type="datetime-local"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            className="rounded-sm border border-wpborder px-3 py-2"
          />
        </label>
      </div>

      {actor && (
        <Notice kind="info" dismissible={false}>
          「{actor.name}」の操作だけを表示しています。{' '}
          <button
            type="button"
            onClick={() => {
              setActor(null);
              setPage(1);
            }}
            className="text-wpaccent underline"
          >
            絞り込みを解除する
          </button>
        </Notice>
      )}

      <ListTable<AuditLog>
        caption="監査ログ一覧"
        rows={query.data?.items ?? []}
        columns={COLUMNS}
        rowKey={(log) => log.id}
        state={state}
        onRetry={() => void query.refetch()}
        rowActions={[
          {
            label: 'この人の操作だけ表示',
            onSelect: (log) => {
              if (!log.actor_user_id) return;
              setActor({ id: log.actor_user_id, name: log.actor_name ?? '（不明）' });
              setPage(1);
            },
          },
        ]}
        emptyTitle="条件に一致するログはありません"
        page={page}
        perPage={PER_PAGE}
        total={query.data?.total ?? 0}
        onPageChange={setPage}
      />
    </>
  );
}
